import type {
  BankImportDecision,
  BankImportPreview,
  BankImportResult,
  CreateInvoiceDraftInput,
  CreatePaymentInput,
  EfacturaActionResult,
  InvoiceStatus,
  PaymentStatus,
  SagaImportPreview,
  SagaImportResult,
  UpdateInvoiceDraftInput,
} from '@financiar/shared';
import { apiClient } from '../../api/client';
import { filenameFromDisposition, rethrowBlobAsApiError } from '../../lib/download';
import type { InvoiceDetail, InvoiceListItem, InvoiceSummary, Paginated, SeriesPreview } from '../../api/types';

/** Everything `GET /invoices` accepts (`listInvoicesQuerySchema`). Note what
 * is NOT here: there is no "stornoed" filter server-side — the list page
 * derives that one client-side from `stornoedByIds`. */
export interface ListInvoicesParams {
  companyId: string;
  page: number;
  pageSize: number;
  search?: string;
  status?: InvoiceStatus;
  paymentStatus?: PaymentStatus;
  /** Issued invoices not fully paid (server maps to payment_status <> PAID). */
  unpaid?: boolean;
  /** Issued, not fully paid, past due date (restante). */
  overdue?: boolean;
  clientIds?: string[];
  seriesId?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'number' | 'issueDate' | 'client' | 'total' | 'remaining';
  sortOrder?: 'asc' | 'desc';
}

/** The list page plus the filtered totals (summed across ALL pages) for the
 * table's total row. */
export interface FacturiPage extends Paginated<InvoiceListItem> {
  totals: { total: string; remaining: string };
}

export async function fetchFacturi(params: ListInvoicesParams): Promise<FacturiPage> {
  const { data } = await apiClient.get<FacturiPage>('/invoices', { params });
  return data;
}

/** The SCOPE filters `GET /invoices/summary` accepts — a subset of
 * `ListInvoicesParams` (no status/payment/sort/pagination). The four stat
 * cards summarize the whole ISSUED set within the selected period, client,
 * series and search, independent of the table's status chips. */
export interface FacturiSummaryParams {
  companyId: string;
  clientIds?: string[];
  seriesId?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

export async function fetchFacturiSummary(params: FacturiSummaryParams): Promise<InvoiceSummary> {
  const { data } = await apiClient.get<InvoiceSummary>('/invoices/summary', { params });
  return data;
}

export async function fetchFactura(id: string): Promise<InvoiceDetail> {
  const { data } = await apiClient.get<InvoiceDetail>(`/invoices/${id}`);
  return data;
}

export async function createFacturaDraft(input: CreateInvoiceDraftInput): Promise<InvoiceDetail> {
  const { data } = await apiClient.post<InvoiceDetail>('/invoices', input);
  return data;
}

export async function updateFacturaDraft(id: string, input: UpdateInvoiceDraftInput): Promise<InvoiceDetail> {
  const { data } = await apiClient.patch<InvoiceDetail>(`/invoices/${id}`, input);
  return data;
}

export async function deleteFactura(id: string): Promise<void> {
  await apiClient.delete(`/invoices/${id}`);
}

/** Allocates the number and freezes the document. `issueDate` is omitted so
 * the server uses today-in-Bucharest — the UI never lets a user back-date an
 * invoice (gapless numbering is chronological). */
export async function issueFactura(id: string): Promise<InvoiceDetail> {
  const { data } = await apiClient.post<InvoiceDetail>(`/invoices/${id}/issue`, {});
  return data;
}

/** Returns the NEW credit note (created and issued in one transaction). */
export async function stornoFactura(id: string): Promise<InvoiceDetail> {
  const { data } = await apiClient.post<InvoiceDetail>(`/invoices/${id}/storno`, {});
  return data;
}

export async function cancelFactura(id: string): Promise<InvoiceDetail> {
  const { data } = await apiClient.post<InvoiceDetail>(`/invoices/${id}/cancel`, {});
  return data;
}

/** Returns the new DRAFT built from this invoice's commercial content. */
export async function duplicateFactura(id: string): Promise<InvoiceDetail> {
  const { data } = await apiClient.post<InvoiceDetail>(`/invoices/${id}/duplicate`, {});
  return data;
}

/** Both payment endpoints return the RECOMPUTED invoice, not the payment —
 * `paidAmount`/`paymentStatus` change with every insert/delete. */
export async function createPayment(id: string, input: CreatePaymentInput): Promise<InvoiceDetail> {
  const { data } = await apiClient.post<InvoiceDetail>(`/invoices/${id}/payments`, input);
  return data;
}

export async function deletePayment(id: string, paymentId: string): Promise<InvoiceDetail> {
  const { data } = await apiClient.delete<InvoiceDetail>(`/invoices/${id}/payments/${paymentId}`);
  return data;
}

/** `GET /series/:id/preview` — the number the next issue WOULD allocate.
 * Never allocates, so it is safe to poll on every series change. */
export async function fetchSeriesPreview(seriesId: string): Promise<SeriesPreview> {
  const { data } = await apiClient.get<SeriesPreview>(`/series/${seriesId}/preview`);
  return data;
}

/** A plain URL rather than an axios call: the PDF is opened by the browser
 * itself (`<a target="_blank">`), and the API serves it `inline` with cookie
 * auth over the same origin — exactly like `logoUrl` in features/firme. */
export function facturaPdfUrl(id: string): string {
  return `/api/v1/invoices/${id}/pdf`;
}

/** `POST /invoices/pdf-archive` — the given invoices' PDFs bundled in a ZIP.
 * No timeout: invoices not yet cached are rendered one by one. */
export async function downloadFacturiPdfArchive(ids: string[]): Promise<{ blob: Blob; filename: string }> {
  try {
    const response = await apiClient.post<Blob>('/invoices/pdf-archive', { ids }, { responseType: 'blob', timeout: 0 });
    const filename = filenameFromDisposition(response.headers['content-disposition'] as string | undefined);
    return { blob: response.data, filename: filename ?? 'Facturi.zip' };
  } catch (error) {
    return rethrowBlobAsApiError(error);
  }
}

// --- e-Factura (ANAF) -----------------------------------------------------

/** Generate the UBL 2.1 XML and upload the invoice to ANAF SPV. */
export async function sendEfactura(id: string): Promise<EfacturaActionResult> {
  const { data } = await apiClient.post<EfacturaActionResult>(`/invoices/${id}/efactura/send`, {});
  return data;
}

/** Poll ANAF for the processing verdict of a previously-sent invoice. */
export async function checkEfacturaStatus(id: string): Promise<EfacturaActionResult> {
  const { data } = await apiClient.post<EfacturaActionResult>(`/invoices/${id}/efactura/check-status`, {});
  return data;
}

/** Mark an invoice as already sent to SPV (from another system), optionally
 * with a known ANAF upload index. */
export async function markEfacturaSent(id: string, uploadIndex?: string): Promise<EfacturaActionResult> {
  const { data } = await apiClient.post<EfacturaActionResult>(
    `/invoices/${id}/efactura/mark-sent`,
    uploadIndex ? { uploadIndex } : {},
  );
  return data;
}

/** Plain URL — the browser downloads the generated XML directly (attachment,
 * cookie auth, same origin), like `facturaPdfUrl`. */
export function efacturaXmlUrl(id: string): string {
  return `/api/v1/invoices/${id}/efactura-xml`;
}

/** Plain URL — the browser downloads the signed ANAF response archive (ZIP). */
export function efacturaDownloadUrl(id: string): string {
  return `/api/v1/invoices/${id}/efactura/download`;
}

/** Plain URL — the browser downloads the official ANAF PDF (the UBL XML
 * rendered via ANAF's public XML→PDF transform), like `efacturaXmlUrl`. */
export function efacturaPdfUrl(id: string): string {
  return `/api/v1/invoices/${id}/efactura-pdf`;
}

/**
 * Historical-invoice import from a Saga XML export. Unlike the client
 * import, neither call takes a `companyId` — the issuing firm is detected
 * (and, on commit, created if missing) straight from the XML's own
 * `FurnizorCIF`, both server-side. `file` is sent as multipart form-data;
 * axios sets the multipart Content-Type + boundary automatically for a
 * `FormData` body.
 */
export async function previewSagaImport(file: File): Promise<SagaImportPreview> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<SagaImportPreview>('/invoices/import/saga/preview', formData);
  return data;
}

/** Re-uploads the SAME file rather than sending back the preview rows — the
 * XML is the single source of truth for what actually gets written. */
export async function commitSagaImport(file: File): Promise<SagaImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<SagaImportResult>('/invoices/import/saga', formData);
  return data;
}

/**
 * Bank statement import (Raiffeisen .xlsx or a .zip of several) — client
 * payments matched against issued invoices. Like the Saga import, neither
 * call takes a `companyId`: the issuing firm is detected from each
 * statement's own "Cod unic:" header field, entirely server-side. Multiple
 * files can be uploaded at once (several monthly statements, or a .zip).
 */
export async function previewBankImport(files: File[]): Promise<BankImportPreview> {
  const formData = new FormData();
  for (const file of files) formData.append('files', file);
  const { data } = await apiClient.post<BankImportPreview>('/bank-import/preview', formData);
  return data;
}

/** Re-uploads the SAME files (never trusts rows echoed back from the
 * preview) plus the user's per-row decisions (keyed by `dedupHash`), sent as
 * a JSON-encoded form field alongside them. */
export async function commitBankImport(files: File[], decisions: Record<string, BankImportDecision>): Promise<BankImportResult> {
  const formData = new FormData();
  for (const file of files) formData.append('files', file);
  formData.append('decisions', JSON.stringify(decisions));
  const { data } = await apiClient.post<BankImportResult>('/bank-import/commit', formData);
  return data;
}
